import { useState, type FormEvent } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { apiErrorMessage } from '@/api/apiError';
import { useCreateOrgTokenMutation } from '@/api/hubApi';
import type { CreateOrgTokenResponse, OrgType, SponsorKind } from '@/api/orgTokenTypes';

type OrgSource = 'existing' | 'new';

interface CreateOrgTokenDialogProps {
    open: boolean;
    onClose: () => void;
}

export function CreateOrgTokenDialog({ open, onClose }: CreateOrgTokenDialogProps) {
    const [orgType, setOrgType] = useState<OrgType>('hospital');
    const [orgSource, setOrgSource] = useState<OrgSource>('new');
    const [orgId, setOrgId] = useState('');
    const [name, setName] = useState('');
    const [kind, setKind] = useState<SponsorKind>('organization');
    const [physicalAddress, setPhysicalAddress] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [label, setLabel] = useState('');
    const [ttlHours, setTtlHours] = useState('');
    const [result, setResult] = useState<CreateOrgTokenResponse | null>(null);
    const [copied, setCopied] = useState(false);

    const [createOrgToken, { isLoading, error }] = useCreateOrgTokenMutation();

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();
        const response = await createOrgToken({
            org_type: orgType,
            org_id: orgSource === 'existing' ? orgId.trim() : undefined,
            name: orgSource === 'new' ? name.trim() : undefined,
            kind: orgType === 'sponsor' && orgSource === 'new' ? kind : undefined,
            physical_address:
                orgType === 'hospital' && orgSource === 'new'
                    ? physicalAddress.trim() || undefined
                    : undefined,
            contact_email: orgSource === 'new' ? contactEmail.trim() || undefined : undefined,
            label: label.trim() || undefined,
            ttl_hours: ttlHours ? Number(ttlHours) : undefined,
        }).unwrap();
        setResult(response);
    }

    async function handleCopy() {
        if (!result) return;
        await navigator.clipboard.writeText(result.token);
        setCopied(true);
    }

    // Dialog is `keepMounted={false}` (the default), so this component
    // unmounts on close and every field — including `result` — resets by
    // itself the next time it opens. Nothing to clean up here.

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Create organization</DialogTitle>
            <Box component="form" onSubmit={handleSubmit}>
                <DialogContent>
                    {result ? (
                        <Alert
                            severity="success"
                            action={
                                <Tooltip title={copied ? 'Copied' : 'Copy token'}>
                                    <IconButton color="inherit" size="small" onClick={handleCopy}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            }
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {result.org_created
                                    ? 'Organization created and token minted'
                                    : 'Token minted'}{' '}
                                for {result.org.name} ({result.org_type})
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}
                            >
                                {result.token}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Shown once — the hub only stores its hash. Copy it now.
                            </Typography>
                        </Alert>
                    ) : (
                        <Stack spacing={3} sx={{ mt: 0.5 }}>
                            {error && (
                                <Alert severity="error">
                                    {apiErrorMessage(error, 'Failed to create org token')}
                                </Alert>
                            )}

                            <FormControl>
                                <InputLabel id="org-type-label">Organization type</InputLabel>
                                <Select
                                    labelId="org-type-label"
                                    label="Organization type"
                                    value={orgType}
                                    onChange={(event: SelectChangeEvent) =>
                                        setOrgType(event.target.value as OrgType)
                                    }
                                >
                                    <MenuItem value="hospital">Hospital</MenuItem>
                                    <MenuItem value="sponsor">Sponsor</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Organization</FormLabel>
                                <RadioGroup
                                    row
                                    value={orgSource}
                                    onChange={(event) =>
                                        setOrgSource(event.target.value as OrgSource)
                                    }
                                >
                                    <FormControlLabel
                                        value="new"
                                        control={<Radio />}
                                        label="Create new"
                                    />
                                    <FormControlLabel
                                        value="existing"
                                        control={<Radio />}
                                        label="Use existing (by id)"
                                    />
                                </RadioGroup>
                            </FormControl>

                            {orgSource === 'existing' ? (
                                <TextField
                                    required
                                    label={`${orgType} id`}
                                    value={orgId}
                                    onChange={(event) => setOrgId(event.target.value)}
                                    placeholder="uuid"
                                />
                            ) : (
                                <>
                                    <TextField
                                        required
                                        label="Name"
                                        value={name}
                                        onChange={(event) => setName(event.target.value)}
                                    />
                                    {orgType === 'sponsor' && (
                                        <FormControl>
                                            <InputLabel id="kind-label">Kind</InputLabel>
                                            <Select
                                                labelId="kind-label"
                                                label="Kind"
                                                value={kind}
                                                onChange={(event: SelectChangeEvent) =>
                                                    setKind(event.target.value as SponsorKind)
                                                }
                                            >
                                                <MenuItem value="organization">
                                                    Organization
                                                </MenuItem>
                                                <MenuItem value="fund">Fund</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                    {orgType === 'hospital' && (
                                        <TextField
                                            required
                                            label="Physical address"
                                            value={physicalAddress}
                                            onChange={(event) =>
                                                setPhysicalAddress(event.target.value)
                                            }
                                        />
                                    )}
                                    <TextField
                                        required
                                        label="Contact email"
                                        type="email"
                                        value={contactEmail}
                                        onChange={(event) => setContactEmail(event.target.value)}
                                    />
                                </>
                            )}

                            <TextField
                                label="Label"
                                helperText="Optional note to identify this token later"
                                value={label}
                                onChange={(event) => setLabel(event.target.value)}
                            />
                            <TextField
                                label="Expires after (hours)"
                                helperText="Optional — leave empty for a standing, non-expiring token"
                                type="number"
                                value={ttlHours}
                                onChange={(event) => setTtlHours(event.target.value)}
                            />
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    {result ? (
                        <Button onClick={onClose} variant="contained">
                            Done
                        </Button>
                    ) : (
                        <>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button type="submit" variant="contained" disabled={isLoading}>
                                {isLoading ? 'Creating…' : 'Create token'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
}
